import React, { useState, useEffect } from "react";
import Dropdown from "components/dropdown";
import { FiAlignJustify } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { IoMdNotificationsOutline } from "react-icons/io";
import { DEFAULT_USER_AVATAR } from "components/ui/UserAvatar";
import { resolveStoredUploadPreview } from "lib/storedUploadImage";
import { applyLanguage, getStoredLanguage, type AppLanguage } from "lib/language";
import { initializeTheme } from "lib/theme";
import { useAppLanguage } from "hooks/useAppLanguage";
import { authStorage, type AuthGuard } from "services/authStorage";

function adminNotificationPath(notification: {
  id?: number | string | null;
  target_type?: string | null;
  target_data?: Record<string, unknown> | null;
}): string {
  const targetType = String(notification.target_type ?? "none");
  const targetData = notification.target_data ?? {};
  const courseId = targetData.course_id;

  if (targetType === "join_requests") {
    return "/admin/join-requests";
  }
  if (targetType === "wallet_topup_requests" || targetType === "wallet") {
    return "/admin/wallets";
  }
  if (targetType === "support_requests") {
    return "/admin/support-requests";
  }
  if (targetType === "course_chat" && courseId) {
    return "/admin/courses/" + String(courseId) + "/chat";
  }
  if (targetType === "url" && typeof targetData.url === "string" && targetData.url) {
    return targetData.url;
  }

  return notification.id ? "/admin/notifications/" + String(notification.id) : "/admin/notifications";
}

const Navbar = (props: {
  onOpenSidenav: () => void;
  brandText: string;
  secondary?: boolean | string;
  authGuard?: AuthGuard;
  profileService: {
    getProfile: () => Promise<any>;
    logout: () => void;
  };
  notificationsService?: {
    getNotifications: () => Promise<any>;
  };
}) => {
  const {
    onOpenSidenav,
    brandText,
    authGuard = "admin",
    profileService,
    notificationsService,
  } = props;
  const navigate = useNavigate();
  const language = useAppLanguage();
  const [adminName, setAdminName] = useState(authGuard === "instructor" ? "Instructor" : "Admin");
  const [profileImage, setProfileImage] = useState<string>(DEFAULT_USER_AVATAR);
  const [notifications, setNotifications] = useState<any[]>([]);
  const guard = authGuard;
  const portalStyles =
    guard === "instructor"
      ? {
          border: "border-orange-200/80 dark:border-orange-400/20",
          badge: "bg-orange-500/10 text-orange-700 dark:bg-orange-400/15 dark:text-orange-300",
        }
      : {
          border: "border-emerald-200/80 dark:border-emerald-400/20",
          badge: "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300",
        };
  const portalLabel =
    language === "ar"
      ? guard === "instructor"
        ? "بوابة المحاضر"
        : "لوحة الإدارة"
      : guard === "instructor"
        ? "Instructor Portal"
        : "Super Admin";

  useEffect(() => {
    const initialLanguage = getStoredLanguage();
    applyLanguage(initialLanguage);
    initializeTheme();

    const fetchProfile = async () => {
      try {
        const user = await profileService.getProfile();
        setAdminName(user?.name || user?.phone || user?.email || (guard === "instructor" ? "Instructor" : "Admin"));
        setProfileImage(
          resolveStoredUploadPreview(user?.image, user?.image_url) || DEFAULT_USER_AVATAR
        );
      } catch {
        // Profile fetch failed, keep default name
      }
    };

    const fetchNotifications = async () => {
      try {
        if (notificationsService) {
          const res = await notificationsService.getNotifications();
          const list = res?.data || res || [];
          setNotifications(Array.isArray(list) ? list.slice(0, 5) : []);
        } else {
          setNotifications([]);
        }
      } catch {
        // Notifications fetch failed, keep empty
      }
    };

    fetchProfile();
    fetchNotifications();
  }, [guard, notificationsService, profileService]);

  const toggleLanguage = () => {
    const next: AppLanguage = language === "ar" ? "en" : "ar";
    applyLanguage(next);
  };
  const isArabic = language === "ar";

  return (
    <nav className={`apple-nav sticky top-2 z-40 flex w-full flex-row flex-wrap items-center justify-between gap-2 border p-2 sm:top-4 sm:gap-4 ${portalStyles.border}`}>
      {/* Left: mobile hamburger + brand block */}
      <div className={`flex min-w-0 flex-1 items-center gap-2 ${isArabic ? "text-right" : ""}`}>
        <button
          type="button"
          aria-label={isArabic ? "فتح القائمة" : "Open menu"}
          onClick={onOpenSidenav}
            className="apple-icon-button flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-gray-600 transition hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-white/[0.06] xl:hidden"
        >
          <FiAlignJustify className="h-5 w-5" />
        </button>

        <div className={`min-w-0 flex-1 ${isArabic ? "text-right" : "text-left"}`}>
          <div className="mb-0.5 hidden flex-wrap items-center gap-x-1 text-[11px] sm:flex sm:text-xs">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${portalStyles.badge}`}>
              {portalLabel}
            </span>
            <span className="text-gray-400 dark:text-slate-500">/</span>
            <Link
              className="font-normal text-navy-700 hover:underline dark:text-slate-300"
              to={authStorage.dashboardPath(guard)}
            >
              {isArabic ? "الصفحات" : "Pages"}
            </Link>
            <span className="text-gray-400 dark:text-slate-500">/</span>
            <span className="capitalize text-navy-700 dark:text-slate-300">{brandText}</span>
          </div>
          <p className="truncate text-lg font-bold capitalize leading-tight text-navy-700 dark:text-slate-50 sm:text-2xl xl:text-[28px]">
            {brandText}
          </p>
        </div>
      </div>

      {/* Right: actions pill */}
      <div className="apple-nav-actions relative flex h-12 w-max shrink-0 items-center justify-around gap-3 rounded-full border px-3 py-1.5 shadow-sm sm:h-[54px] sm:gap-4 sm:px-4 sm:py-2">
        {/* Notification */}
        <Dropdown
          button={
            <button
              type="button"
              aria-label={isArabic ? "الإشعارات" : "Notifications"}
              className="apple-icon-button flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-gray-600 transition hover:bg-gray-100 dark:text-white dark:hover:bg-white/[0.06]"
            >
              <IoMdNotificationsOutline className="h-5 w-5" />
            </button>
          }
          animation="origin-[65%_0%] md:origin-top-right transition-all duration-300 ease-in-out"
          children={
            <div
              dir={isArabic ? "rtl" : "ltr"}
              className="apple-popover flex w-[min(88vw,420px)] flex-col gap-3 p-4 sm:w-[min(92vw,460px)]"
            >
              <div className="flex items-center justify-between">
                <p className="text-base font-bold text-navy-700 dark:text-white">
                  {isArabic ? "الإشعارات" : "Notification"}
                </p>
                <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                  {isArabic ? "تحديد الكل كمقروء" : "Mark all read"}
                </p>
              </div>

              {notifications.length > 0 ? (
                notifications.map((notif: any, index: number) => (
                  <button
                    key={notif.id ?? index}
                    type="button"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={() => {
                      const path = adminNotificationPath(notif);
                      if (path.startsWith("http://") || path.startsWith("https://")) {
                        window.open(path, "_blank", "noopener,noreferrer");
                      } else {
                        navigate(path);
                      }
                    }}
                    className="flex w-full items-center gap-2 rounded-xl p-1 text-start transition hover:bg-gray-50 dark:hover:bg-white/5"
                    aria-label={isArabic ? "فتح الإشعار" : "Open notification"}
                  >
                    <div className="flex h-full w-[64px] shrink-0 items-center justify-center rounded-xl bg-gradient-to-b from-brandLinear to-brand-500 py-4 text-2xl text-white sm:w-[85px]">
                      <IoMdNotificationsOutline />
                    </div>
                    <div
                      className={`flex h-full min-w-0 flex-1 flex-col justify-center rounded-lg px-1 text-sm ${
                        isArabic ? "text-right" : "text-left"
                      }`}
                    >
                      <p className="mb-1 truncate text-sm font-bold text-gray-900 dark:text-white sm:text-base">
                        {notif.title || notif.type || (isArabic ? "إشعار" : "Notification")}
                      </p>
                      <p className="font-base line-clamp-2 text-xs text-gray-900 dark:text-white">
                        {notif.body || notif.message || notif.data || ""}
                      </p>
                    </div>
                  </button>
                ))
              ) : (
                <div className="py-4 text-center text-sm text-gray-500">
                  {isArabic ? "لا توجد إشعارات" : "No notifications"}
                </div>
              )}
            </div>
          }
          classNames={`py-2 top-4 w-max ${
            isArabic ? "left-0" : "right-0"
          }`}
        />
        <button
          type="button"
          className="cursor-pointer text-gray-600"
          onClick={toggleLanguage}
          title={language === "ar" ? "Switch to English" : "التبديل للعربية"}
        >
          <span className="text-[11px] font-bold uppercase text-gray-600 dark:text-white">
            {language === "ar" ? "EN" : "AR"}
          </span>
        </button>
        {/* Profile & Dropdown */}
        <Dropdown
          button={
            <img
              className="apple-avatar h-8 w-8 shrink-0 cursor-pointer rounded-full object-cover sm:h-10 sm:w-10"
              src={profileImage}
              alt="Admin"
              onError={() => setProfileImage(DEFAULT_USER_AVATAR)}
            />
          }
          children={
            <div
              dir={isArabic ? "rtl" : "ltr"}
              className="apple-popover flex h-48 w-56 flex-col justify-start bg-cover bg-no-repeat"
            >
              <div className="mx-4 mt-3">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-bold text-navy-700 dark:text-white">
                    {adminName}
                  </p>
                </div>
              </div>
              <div className="mt-3 h-px w-full bg-gray-200 dark:bg-white/20 " />

              <div className="mx-4 mt-3 flex flex-col">
                <Link
                  to={guard === "instructor" ? "/instructor/profile" : "/admin/profile"}
                  className="text-sm text-gray-800 dark:text-white hover:dark:text-white"
                >
                  {isArabic ? "إعدادات الملف الشخصي" : "Profile Settings"}
                </Link>
                <a
                  href=" "
                  className="mt-3 text-sm font-medium text-red-500 hover:text-red-500"
                  onClick={() => {
                    profileService.logout();
                  }}
                >
                  {isArabic ? "تسجيل الخروج" : "Log Out"}
                </a>
              </div>
            </div>
          }
          classNames={`py-2 top-8 w-max ${isArabic ? "left-0" : "right-0"}`}
        />
      </div>
    </nav>
  );
};

export default Navbar;
